import { useState, useEffect, useRef } from 'react';
import { PLANBASE_CONFIG } from '../config';

interface ProjectViewsResult {
  views: number;
  loading: boolean;
}

// Local storage key for views cache
const VIEWS_CACHE_KEY = 'planbase_views_cache';

// Get cached views count
function getCachedViews(pageId: string): number | null {
  try {
    const cache = JSON.parse(localStorage.getItem(VIEWS_CACHE_KEY) || '{}');
    return cache[pageId] ?? null;
  } catch {
    return null;
  }
}

// Set cached views count
function setCachedViews(pageId: string, count: number): void {
  try {
    const cache = JSON.parse(localStorage.getItem(VIEWS_CACHE_KEY) || '{}');
    cache[pageId] = count;
    localStorage.setItem(VIEWS_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Silent fail
  }
}

export function useProjectViews(projectId: string | undefined): ProjectViewsResult {
  const [views, setViews] = useState(0);
  const [loading, setLoading] = useState(true);
  const hasTracked = useRef(false);

  // Initialize from cache immediately (optimistic)
  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    const pageId = `project_${projectId}`;
    const cachedViews = getCachedViews(pageId);
    if (cachedViews !== null) {
      setViews(cachedViews);
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    const trackAndFetchViews = async () => {
      try {
        const pageId = `project_${projectId}`;
        
        // Only track view once per component mount
        if (!hasTracked.current) {
          hasTracked.current = true;
          
          // Check if we recently viewed this project (throttle to once per 30 seconds)
          const lastViewKey = `last_view_${projectId}`;
          const lastViewTime = localStorage.getItem(lastViewKey);
          const now = Date.now();
          const shouldTrack = !lastViewTime || (now - parseInt(lastViewTime)) > 30000;

          if (shouldTrack) {
            localStorage.setItem(lastViewKey, now.toString());
            
            // Optimistic update - increment local count immediately
            const currentCached = getCachedViews(pageId) || views;
            const optimisticViews = currentCached + 1;
            setViews(optimisticViews);
            setCachedViews(pageId, optimisticViews);
            
            // POST to increment view count
            const trackResponse = await fetch(
              `${PLANBASE_CONFIG.apiUrl}/sdk/analytics/page-view`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
                  'X-Project-ID': PLANBASE_CONFIG.projectId,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({ page_id: pageId }),
              }
            );

            if (trackResponse.ok) {
              const trackData = await trackResponse.json();
              const serverViews = trackData.data?.views || optimisticViews;
              setViews(serverViews);
              setCachedViews(pageId, serverViews);
              setLoading(false);
              return;
            }
          }
        }

        // If we didn't track (throttled), just fetch current count
        const response = await fetch(
          `${PLANBASE_CONFIG.apiUrl}/sdk/analytics/page-views?page_id=${encodeURIComponent(pageId)}`,
          {
            headers: {
              'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`,
              'X-Project-ID': PLANBASE_CONFIG.projectId,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          const serverViews = data.data?.views || 0;
          setViews(serverViews);
          setCachedViews(pageId, serverViews);
        }
      } catch (error) {
        // Keep using cached values on error
      } finally {
        setLoading(false);
      }
    };

    trackAndFetchViews();
  }, [projectId]);

  return { views, loading };
}
